import {
  BillingPeriod,
  CacheLocation,
  ChargeBeeAddonId,
  ChargeBeePlan,
  ChargeBeePlanId,
  PlanName,
  PlanTier,
  Support,
} from '@prerender/billing-shared';

import { DEFAULT_DESKTOP_USER_AGENT, DEFAULT_MOBILE_USER_AGENT } from '@prerender/prerender-shared/FE';
import Role from './Role';
import { FeatureFlag } from '../../hooks/FeatureFlag';

type UserSettings = {
  emailPreferences: {
    extraRenderEmailOptIn: boolean;
    sendSuggested: boolean;
    emailReportPeriod: 'WEEKLY';
    send429Alerts: boolean;
    aiDiscoverabilityStatusUpdate?: boolean;
  };
  customViewport: {
    width: number;
    height: number;
  };
  customMobileViewport: {
    width: number;
    height: number;
  };
  pageLoadTimeout: number;
  loadImages: boolean;
  renderRegion: 'shared' | 'us' | 'eu';
  forceHTTPS: boolean;
  customDesktopUserAgent: string;
  customMobileUserAgent: string;
  manualReactivate: boolean;
  statusCodesToCache: string[];
};

export type PrerenderUserResponse = {
  adminEmail: string;
  allURLCount: number;
  amountOf429Renders: number;
  billingPeriod: BillingPeriod;
  cacheFreshness: number;
  cancelledAt: string | null;
  chargeBeePlanId: ChargeBeePlan['chargeBeePlanId'];
  companyId: number;
  companyName: string;
  costPerMonth: number;
  csmId: string;
  customCachedPagesLimit: number | null;
  customPrice: number | null;
  customRecacheDelay: number | null;
  email: string;
  emailVerified: boolean;
  features: typeof FeatureFlag[keyof typeof FeatureFlag][];
  firstName: string | null;
  gettingStartedSteps: {
    integrationWizardCompleted: boolean;
    queryParameters: boolean;
    reviewSitemaps: boolean;
    teamInvited: boolean;
  };
  hasBillingAddress: boolean;
  hasBillingInfo: boolean;
  hasBillingManager: boolean;
  hasCustomContractPricing: boolean;
  hadPaidPlan: boolean;
  hadTrialBefore: boolean;
  id: number;
  ignoreAllQueryParams: boolean;
  isAdmin: boolean;
  isBetaTester: boolean;
  isCacheFreshnessDisabled: boolean;
  isMeteredBilling: boolean;
  isTrial: boolean;
  lastCharged: string;
  lastName: string | null;
  lastSeen: string | null;
  mobileAdaptive: boolean;
  nextBillingAt?: string;
  numPagesCached: number;
  numberOfNewNotifications: number;
  onboardingSurveyCompleted: boolean;
  plan: {
    name: ChargeBeePlan['name'];
    limit: number;
    cacheRefreshInterval: number[];
    estimate: {
      totalInCents: number;
      currencyCode: string;
      planPriceInCents: number;
      addonsPriceInCents: number;
      packagesPriceInCents: number;
    };
    suggested: { name: ChargeBeePlan['name'] } | null;
    current: Omit<ChargeBeePlan, 'includedRenderedPages'> & {
      includedCaches: number;
      id: ChargeBeePlanId;
      cacheLimit: null | number;
    };
  };
  planName: ChargeBeePlan['name'];
  queryParamsWhitelist: boolean;
  recacheDelay: number;
  recachingDisabled: boolean;
  renderCounter: number;
  role: Role;
  settings: UserSettings;
  showRegistrationSteps: boolean;
  signupDate: string; // current user signup date
  companyCreationDate: string; // company creation date
  token: string;
  trackingCodeInstalled: boolean;
  trackingCodeInstalledDate: string | null;
  trialEndsAt: string | null;
  upgradedAt: string | null;
  upgradedFromChargebeePlanId: ChargeBeePlan['chargeBeePlanId'] | null;
  userPausedRecaching: boolean;
  customDeal: {
    id: string;
    user_id: number;
    price: number;
    extra_renders_price: number;
    included_renders: number;
    starts_at: string;
    ends_at: string;
    created_at: string;
    billing_period: string;
  } | null;
};

export const defaultUserResponse: PrerenderUserResponse = {
  id: 1,
  companyId: 1,
  email: 'default-email@prerender.io',
  numPagesCached: 0,
  token: 'aaaaa',
  signupDate: '2024-05-03T10:15:29.634Z',
  companyCreationDate: '2024-05-03T10:15:29.634Z',
  trackingCodeInstalled: true,
  trackingCodeInstalledDate: '2024-09-10T12:23:39.448Z',
  cacheFreshness: 28,
  hasBillingInfo: false,
  recachingDisabled: false,
  planName: PlanName.Starter,
  costPerMonth: 0,
  emailVerified: true,
  customPrice: null,
  hasCustomContractPricing: false,
  customCachedPagesLimit: null,
  recacheDelay: 60000,
  userPausedRecaching: false,
  queryParamsWhitelist: true,
  ignoreAllQueryParams: false,
  hasBillingAddress: false,
  isMeteredBilling: true,
  gettingStartedSteps: {
    integrationWizardCompleted: true,
    queryParameters: false,
    reviewSitemaps: false,
    teamInvited: false,
  },
  plan: {
    name: PlanName.Starter,
    limit: 25000,
    cacheRefreshInterval: [3, 3],
    estimate: {
      totalInCents: 0,
      currencyCode: 'USD',
      planPriceInCents: 0,
      addonsPriceInCents: 0,
      packagesPriceInCents: 0,
    },
    suggested: null,
    current: {
      name: PlanName.Starter,
      chargeBeePlanId: ChargeBeePlanId.Starter,
      index: 1,
      legacy: false,
      costInCents: 34900,
      addonCostInCents: 125,
      currencyCode: 'USD',
      includedCaches: 500000,
      chargeBeeAddonId: ChargeBeeAddonId.starter2025Overages,
      suggested: false,
      current: false,
      cacheRefreshInterval: [0.25, 30],
      cacheLocation: CacheLocation.both,
      sitemapRevisitInterval: [8, 168],
      dataRetentionDays: 730,
      available: false,
      support: Support.high,
      billingPeriod: BillingPeriod.monthly,
      customPlan: false,
      contactUsToUse: false,
      tier: PlanTier.P2025,
      apiAccess: true,
      auditLog: false,
      domainSchedulerSettingsMaxRules: 0,
      dashboardAlerts: true,
      searchPerformanceMaxDomains: 0,
      id: ChargeBeePlanId.Starter,
      cacheLimit: null,
    },
  },
  firstName: 'Firstname',
  lastName: null,
  cancelledAt: null,
  renderCounter: 0,
  chargeBeePlanId: ChargeBeePlanId.Starter,
  mobileAdaptive: true,
  settings: {
    emailPreferences: {
      extraRenderEmailOptIn: true,
      sendSuggested: true,
      emailReportPeriod: 'WEEKLY',
      send429Alerts: true,
      aiDiscoverabilityStatusUpdate: true,
    },
    customViewport: {
      width: 1440,
      height: 718,
    },
    customMobileViewport: {
      width: 375,
      height: 667,
    },
    pageLoadTimeout: 20000,
    loadImages: false,
    renderRegion: 'shared',
    forceHTTPS: true,
    customDesktopUserAgent: DEFAULT_DESKTOP_USER_AGENT,
    customMobileUserAgent: DEFAULT_MOBILE_USER_AGENT,
    manualReactivate: false,
    statusCodesToCache: [],
  },
  isBetaTester: false,
  features: [
    FeatureFlag.Oversight,
    FeatureFlag.DomainsDetails,
    FeatureFlag.DomainSchedulerSettings,
    FeatureFlag.SeoReport,
    FeatureFlag.Pricing2025,
    FeatureFlag.SearchPerformance,
    FeatureFlag.SearchPerformance,
  ],
  allURLCount: 100,
  companyName: 'default-email@company.com',
  adminEmail: 'default-email@company.com',
  customRecacheDelay: null,
  isTrial: false,
  isCacheFreshnessDisabled: false,
  // nextBillingAt: '2024-06-03T10:15:29.634Z',
  lastCharged: '2024-05-03T10:15:29.634Z',
  upgradedFromChargebeePlanId: null,
  upgradedAt: null,
  showRegistrationSteps: false,
  isAdmin: true,
  role: Role.SuperAdmin,
  billingPeriod: BillingPeriod.monthly,
  hadPaidPlan: false,
  csmId: 'aaaaa',
  numberOfNewNotifications: 3,
  hasBillingManager: false,
  hadTrialBefore: false,
  trialEndsAt: null,
  amountOf429Renders: 0,
  customDeal: null,
  onboardingSurveyCompleted: true,
  lastSeen: '2024-05-03T10:15:29.634Z',
};
